import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faSpinner,
  faCheckCircle,
  faTimes,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import './css/ExportModal.css';

/** Fake bar eases toward this while waiting for the export request to be accepted. */
const FAKE_PROGRESS_HOLD_CAP = 88;
const FAKE_PROGRESS_TICK_MS = 200;
/** Fraction of remaining (cap − current) closed each tick — asymptotic ease. */
const FAKE_PROGRESS_RATE = 0.045;
/** Short illusion after the job is accepted, then confirmation. */
const ILLUSION_COMPLETE_MS = 2600;
const CONFIRM_AFTER_COMPLETE_MS = 400;

const ExportModal = ({ onSubmit, onComplete, onClose, userEmail }) => {
  const skipEmailStep = Boolean(userEmail);
  const [step, setStep] = useState(skipEmailStep ? 'processing' : 'email');
  const [email, setEmail] = useState(userEmail || '');
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const hasAutoStartedRef = useRef(false);
  const fakeProgressTimerRef = useRef(null);
  const completeTimeoutRef = useRef(null);
  const emailInputRef = useRef(null);
  const dialogRef = useRef(null);
  const progressRef = useRef(0);

  const stopFakeProgress = useCallback(() => {
    if (fakeProgressTimerRef.current) {
      clearInterval(fakeProgressTimerRef.current);
      fakeProgressTimerRef.current = null;
    }
  }, []);

  const setProgressSafe = useCallback((value) => {
    progressRef.current = value;
    setProgress(value);
  }, []);

  /** Asymptotic progress while the create-export request is in flight. */
  const startHoldingFakeProgress = useCallback(() => {
    stopFakeProgress();
    setProgressSafe(4);
    fakeProgressTimerRef.current = setInterval(() => {
      setProgressSafe(
        (() => {
          const prev = progressRef.current;
          if (prev >= FAKE_PROGRESS_HOLD_CAP) return FAKE_PROGRESS_HOLD_CAP;
          const next = prev + (FAKE_PROGRESS_HOLD_CAP - prev) * FAKE_PROGRESS_RATE;
          return Math.min(FAKE_PROGRESS_HOLD_CAP, next);
        })()
      );
    }, FAKE_PROGRESS_TICK_MS);
  }, [setProgressSafe, stopFakeProgress]);

  /**
   * After the job is accepted, finish a short progress illusion then show
   * the "you'll get an email" confirmation — no status polling.
   */
  const finishIllusionThenConfirm = useCallback(() => {
    stopFakeProgress();
    if (completeTimeoutRef.current) {
      clearTimeout(completeTimeoutRef.current);
    }

    const startValue = Math.max(progressRef.current, 8);
    const startedAt = Date.now();

    fakeProgressTimerRef.current = setInterval(() => {
      const t = Math.min(1, (Date.now() - startedAt) / ILLUSION_COMPLETE_MS);
      const eased = 1 - Math.pow(1 - t, 2.2);
      const next = startValue + (100 - startValue) * eased;
      setProgressSafe(Math.min(100, next));

      if (t >= 1) {
        stopFakeProgress();
        setProgressSafe(100);
        completeTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setStep('success');
          }
        }, CONFIRM_AFTER_COMPLETE_MS);
      }
    }, 50);
  }, [setProgressSafe, stopFakeProgress]);

  useEffect(() => {
    isMountedRef.current = true;
    // showModal() promotes the dialog into the browser's top layer, so it is
    // positioned and hit-tested independently of any ancestor stacking or
    // scroll container. Nothing in the page can sit over it or offset it.
    if (dialogRef.current && !dialogRef.current.open) {
      dialogRef.current.showModal();
    }
    document.body.classList.add('export-modal-open');
    return () => {
      isMountedRef.current = false;
      document.body.classList.remove('export-modal-open');
      if (fakeProgressTimerRef.current) {
        clearInterval(fakeProgressTimerRef.current);
      }
      if (completeTimeoutRef.current) {
        clearTimeout(completeTimeoutRef.current);
      }
    };
  }, []);

  // ?tapdebug=1 reports what actually receives a tap, so a failure tells us the
  // blocking element instead of leaving us to guess at CSS again.
  const tapDebugEnabled =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('tapdebug');
  const [tapDebug, setTapDebug] = useState('tap anywhere…');

  useEffect(() => {
    if (!tapDebugEnabled) return undefined;

    const describe = (el) => {
      if (!el) return 'nothing';
      const cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/)[0] : '';
      return `${el.tagName.toLowerCase()}${cls ? `.${cls}` : ''}`;
    };

    const onTap = (e) => {
      const point = e.touches?.[0] || e;
      const { clientX: x, clientY: y } = point;
      setTapDebug(
        [
          `pt ${Math.round(x)},${Math.round(y)}`,
          `hit ${describe(document.elementFromPoint(x, y))}`,
          `target ${describe(e.target)}`,
          `scrollY ${Math.round(window.scrollY)}`,
        ].join(' | ')
      );
    };

    document.addEventListener('touchstart', onTap, true);
    document.addEventListener('pointerdown', onTap, true);
    return () => {
      document.removeEventListener('touchstart', onTap, true);
      document.removeEventListener('pointerdown', onTap, true);
    };
  }, [tapDebugEnabled]);

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  // Prefer the live DOM value — iOS autofill often fills the field without
  // firing React onChange until blur, which left Start Export stuck disabled.
  const readEmailValue = () => {
    const fromInput = emailInputRef.current?.value?.trim() || '';
    return fromInput || email.trim();
  };

  const syncEmailFromEvent = (e) => {
    const value = e.target.value;
    setEmail(value);
    setError(null);
    if (showValidation) {
      setShowValidation(false);
    }
  };

  const startExport = useCallback(
    async (emailToUse) => {
      setStep('processing');
      setIsSubmitting(true);
      setError(null);
      setShowValidation(false);
      setEmail(emailToUse);
      startHoldingFakeProgress();

      try {
        const result = await onSubmit(emailToUse);
        if (!isMountedRef.current) return;

        if (!result?.jobId) {
          throw new Error('Export accepted but no job ID returned.');
        }

        // Job accepted — treat as success from the user's POV after a short illusion.
        finishIllusionThenConfirm();
      } catch (err) {
        if (!isMountedRef.current) return;
        stopFakeProgress();
        if (completeTimeoutRef.current) {
          clearTimeout(completeTimeoutRef.current);
          completeTimeoutRef.current = null;
        }
        setError(err.message || 'Export failed. Please try again.');
        setStep(skipEmailStep ? 'failed' : 'email');
        setProgressSafe(0);
      } finally {
        if (isMountedRef.current) {
          setIsSubmitting(false);
        }
      }
    },
    [
      finishIllusionThenConfirm,
      onSubmit,
      setProgressSafe,
      skipEmailStep,
      startHoldingFakeProgress,
      stopFakeProgress,
    ]
  );

  useEffect(() => {
    if (!skipEmailStep || hasAutoStartedRef.current) return;
    hasAutoStartedRef.current = true;
    startExport(userEmail);
  }, [skipEmailStep, userEmail, startExport]);

  const beginExportFromForm = async () => {
    if (isSubmitting) return;
    const emailToUse = readEmailValue();
    setEmail(emailToUse);

    if (!validateEmail(emailToUse)) {
      setShowValidation(true);
      return;
    }

    await startExport(emailToUse);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await beginExportFromForm();
  };

  const handleRetry = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await startExport(userEmail || email);
  };

  const handleSuccessClose = () => {
    if (onComplete) {
      onComplete();
    }
  };

  const modalContent = (
    <dialog
      ref={dialogRef}
      className="export-modal-dialog"
      onCancel={(e) => {
        e.preventDefault();
        if (onClose && step === 'email') {
          onClose();
        }
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose && step === 'email') {
          onClose();
        }
      }}
    >
      <div className="export-modal">
        {tapDebugEnabled && <p className="export-modal-tapdebug">{tapDebug}</p>}
        {(step === 'email' || step === 'failed') && onClose && step !== 'failed' && (
          <button
            className="export-modal-close"
            onClick={onClose}
            type="button"
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}

        {step === 'email' && (
          <>
            <div className="export-modal-header">
              <FontAwesomeIcon icon={faEnvelope} className="modal-icon email-icon" />
              <h2>Get Your Design</h2>
              <p>Enter your email to receive your finished video</p>
            </div>

            <form onSubmit={handleSubmit} className="export-modal-form">
              <div className="input-group">
                <input
                  ref={emailInputRef}
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={syncEmailFromEvent}
                  onInput={syncEmailFromEvent}
                  className={`email-input ${showValidation && !validateEmail(readEmailValue()) ? 'invalid' : ''}`}
                  autoComplete="email"
                  name="email"
                />
                {showValidation && !validateEmail(readEmailValue()) && (
                  <span className="input-error">Please enter a valid email address</span>
                )}
              </div>

              {error && <div className="error-box">{error}</div>}

              <button type="submit" className="modal-button primary" disabled={isSubmitting}>
                {isSubmitting ? 'Starting…' : 'Start Export'}
              </button>
            </form>
          </>
        )}

        {step === 'processing' && (
          <>
            <div className="export-modal-header">
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="modal-icon processing-icon"
              />
              <h2>Generating Your Video</h2>
              <p>Hang tight for a moment…</p>
            </div>

            <div className="progress-section">
              <div className="progress-bar-wrapper">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%` }} />
                </div>
              </div>

              <div className="progress-info">
                <span className="progress-percentage">{Math.round(progress)}%</span>
                <span className="progress-message">Generating your video…</span>
              </div>
            </div>

            <div className="processing-note">
              <p>
                ✨ Your video will be emailed to <strong>{email}</strong>
              </p>
            </div>
          </>
        )}

        {step === 'failed' && (
          <>
            <div className="export-modal-header">
              <FontAwesomeIcon icon={faExclamationTriangle} className="modal-icon error-icon" />
              <h2>Export Failed</h2>
              <p>Something went wrong starting your export. You can try again.</p>
            </div>

            {error && <div className="error-box">{error}</div>}

            <button
              type="button"
              className="modal-button primary"
              onClick={handleRetry}
            >
              Retry Export
            </button>
          </>
        )}

        {step === 'success' && (
          <>
            <div className="export-modal-header">
              <FontAwesomeIcon icon={faCheckCircle} className="modal-icon success-icon" />
              <h2>We&apos;re Generating Your Video</h2>
              <p>You&apos;ll receive an email shortly.</p>
            </div>

            <div className="success-content">
              <div className="success-message-box">
                <p className="success-message-text">
                  ✨ We&apos;re creating your video and will send it to{' '}
                  <strong>{email}</strong>.
                </p>
              </div>

              <div className="success-tip">
                💡 <strong>Tip:</strong> Check your spam folder if you don&apos;t see it within a few
                minutes
              </div>
            </div>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSuccessClose();
              }}
              className="modal-button primary"
              type="button"
            >
              Got it!
            </button>
          </>
        )}
      </div>
    </dialog>
  );

  return createPortal(modalContent, document.body);
};

export default ExportModal;
